import { FormFieldError } from '@tovala/component-library'
import { clsx } from 'clsx'
import { Fragment, ReactNode } from 'react'
import { UseFormRegisterReturn } from 'react-hook-form'

import { events } from 'analytics/events'
import LockIcon from 'components/common/icons/LockIcon'
import LockOpenIcon from 'components/common/icons/LockOpenIcon'
import StarIcon from 'components/common/icons/StarIcon'
import { RatingChip } from 'components/core/service/combinedapi/reviews'
import { useUser } from 'contexts/user'
import { track } from 'utils/analytics'
import { getPublicReviewerName } from 'utils/user'

export type StarsRatingForm = '100' | '80' | '60' | '40' | '20'

export interface MealReviewFormValues {
  comment: string
  rating: StarsRatingForm | ''
  selectedChipIDs: string[]
}

const starsRatings: StarsRatingForm[] = ['100', '80', '60', '40', '20']
const starsRatingSRLabels: Record<StarsRatingForm, string> = {
  '100': '5 stars',
  '80': '4 stars',
  '60': '3 stars',
  '40': '2 stars',
  '20': '1 star',
}

export const RatingStars = ({
  idPrefix,
  mealID,
  registration,
  selectedRating,
  size,
  sourceID,
}: {
  idPrefix: string
  mealID: number
  registration: UseFormRegisterReturn
  selectedRating: number
  size: 'compact' | 'large'
  sourceID: string
}) => {
  return (
    <div
      className={clsx(
        'flex flex-row-reverse items-center',
        size === 'large'
          ? 'mx-auto w-full max-w-[382px] justify-between px-2 pt-2 md:max-w-none'
          : 'w-fit gap-2',
      )}
    >
      {starsRatings.map((rating, i) => {
        const isFilled = selectedRating >= Number(rating)
        const starID = `${idPrefix}-${i}-star`

        return (
          <Fragment key={starID}>
            <label
              className={clsx(
                'peer flex cursor-pointer items-center justify-center',
                {
                  'text-orange-2': isFilled,
                  'text-[#D9D9D9] transition-colors hover:text-orange-2 peer-hover:text-orange-2':
                    !isFilled,
                },
              )}
              htmlFor={starID}
              onClick={() => {
                track(events.ORDERS_MEAL_REIVEW_STAR_TAPPED, {
                  value: rating,
                  meal_id: mealID,
                  source_id: sourceID,
                })
              }}
            >
              <input
                className="sr-only"
                id={starID}
                {...registration}
                type="radio"
                value={rating}
              />
              <span
                className={clsx(
                  'block',
                  size === 'large' ? 'h-[60px] w-[60px]' : 'h-7 w-7',
                )}
              >
                <StarIcon />
                <span className="sr-only">{starsRatingSRLabels[rating]}</span>
              </span>
            </label>
          </Fragment>
        )
      })}
    </div>
  )
}

export const RatingChipOptions = ({
  chips,
  idPrefix,
  mealID,
  registration,
  selectedChipIDs,
}: {
  chips: RatingChip[]
  idPrefix: string
  mealID: number
  registration: UseFormRegisterReturn
  selectedChipIDs: string[]
}) => {
  return (
    <div className="flex flex-wrap gap-2 text-left">
      {chips.map((chip) => {
        const chipID = `${idPrefix}-${chip.id}`

        return (
          <div key={chipID}>
            <input
              className="peer sr-only"
              id={chipID}
              {...registration}
              type="checkbox"
              value={chip.id}
            />

            <ChipLabel
              checked={selectedChipIDs.includes(chip.id)}
              labelFor={chipID}
              onChipClick={() => {
                track(events.ORDERS_MEAL_REVIEW_CHIP_TAPPED, {
                  review_chip_id: chip.id,
                  meal_id: mealID,
                })
              }}
            >
              {chip.name}
            </ChipLabel>
          </div>
        )
      })}
    </div>
  )
}

const ChipLabel = ({
  checked,
  children,
  labelFor,
  onChipClick,
}: {
  checked: boolean
  children: ReactNode
  labelFor: string
  onChipClick(): void
}) => {
  return (
    <label
      className={clsx(
        'inline-block cursor-pointer rounded-full px-3 py-1.5 text-[14px] font-semibold leading-[1.1] transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-black',
        checked
          ? 'bg-orange-2 text-white'
          : 'bg-grey-2 text-black hover:bg-grey-3',
      )}
      htmlFor={labelFor}
      onClick={onChipClick}
    >
      {children}
    </label>
  )
}

export const ReviewCommentField = ({
  errorMessage,
  id,
  onToggleSharePublicly,
  registration,
  sharePublicly,
}: {
  errorMessage: string | undefined
  id: string
  onToggleSharePublicly(): void
  registration: UseFormRegisterReturn
  sharePublicly: boolean
}) => {
  const { user } = useUser()

  return (
    <div className="space-y-2 text-left">
      <label className="text-h/14_120 font-semibold" htmlFor={id}>
        {sharePublicly ? 'Write a review' : 'Give feedback'}{' '}
        <span className="text-grey-9">(optional)</span>
      </label>

      <div
        className={clsx(
          'rounded-lg border bg-grey-0 focus-within:border-black',
          errorMessage ? 'border-red' : 'border-grey-4',
        )}
      >
        <div className="flex items-center gap-2 px-3 pt-3">
          <button
            aria-label={
              sharePublicly ? 'Make review private' : 'Make review public'
            }
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-grey-3 text-black transition-colors hover:bg-grey-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
            onClick={onToggleSharePublicly}
            title={sharePublicly ? 'Make review private' : 'Make review public'}
            type="button"
          >
            {sharePublicly ? <LockOpenIcon /> : <LockIcon />}
          </button>
          <p className="text-k/14_120">
            {sharePublicly ? (
              <>
                Posting publicly as{' '}
                <span className="font-medium">
                  {getPublicReviewerName(user.info.name)}
                </span>
              </>
            ) : (
              'Only visible to our chefs and you'
            )}
          </p>
        </div>
        <textarea
          className="block h-[107px] w-full resize-none rounded-b-lg bg-transparent px-3 pb-3 pt-2 text-k/14_120 placeholder:text-grey-9 focus:outline-none"
          id={id}
          placeholder="Add any feedback you have here"
          {...registration}
        />
      </div>

      {errorMessage && <FormFieldError>{errorMessage}</FormFieldError>}
    </div>
  )
}

export const SharePubliclyToggle = ({
  id,
  registration,
  sharePublicly,
}: {
  id: string
  registration: UseFormRegisterReturn
  sharePublicly: boolean
}) => {
  return (
    <div className="space-y-3 text-left">
      <label
        className="flex cursor-pointer items-center justify-between"
        htmlFor={id}
      >
        <span className="text-h/14_120 font-semibold">Share publicly</span>
        <input
          className="peer sr-only"
          id={id}
          role="switch"
          type="checkbox"
          {...registration}
        />
        <span
          aria-hidden="true"
          className="inline-flex h-[22px] w-12 shrink-0 items-center rounded-full bg-grey-6 transition-colors peer-checked:bg-orange-2 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-black peer-checked:[&>span]:translate-x-[27px]"
        >
          <span className="h-[18px] w-[18px] translate-x-[3px] rounded-full bg-white transition-transform" />
        </span>
      </label>
      <p className="text-grey-10">
        {sharePublicly
          ? 'Your rating and review will be visible to all customers. Your review displays your first name and last initial.'
          : 'Your rating is included in the average score shown to all customers. Your review is visible only to you and Tovala.'}
      </p>
    </div>
  )
}
